import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api/client';
import { identify, reset, Analytics } from '../analytics';
import i18n from '../i18n';
import { thumbCacheClear } from '../utils/thumbCache';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyUser = (u) => {
    setUser(u);
    // Только при реальной смене: changeLanguage на тот же язык всё равно шлёт
    // languageChanged → новая t() во всём приложении → эффекты с t в зависимостях
    // перезапускаются каждые 30 сек (checkSession) и дублируют события PostHog.
    if (u?.language && u.language !== i18n.language) i18n.changeLanguage(u.language);
    if (u?.id != null) identify(u); // привязка аналитики к домену ОРГи + изоляция тенантов
  };

  // Перечитать /me (напр. после смены видимости модулей в Настройках).
  const refreshUser = () => api.me().then(applyUser).catch(() => {});

  useEffect(() => {
    const token = localStorage.getItem('pm_token');
    if (token) {
      api.me()
        .then(applyUser)
        .catch((err) => {
          // Токен удаляем только при явном 401/403 от сервера.
          // Network error (офлайн-запуск через SW-кэш, таймаут) — токен не трогаем,
          // иначе запуск без сети разлогинивает пользователя.
          if (err?.status === 401 || err?.status === 403) {
            localStorage.removeItem('pm_token');
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    // Синхронизация между вкладками — если токен изменился, перечитываем пользователя
    const handleStorage = (e) => {
      if (e.key !== 'pm_token') return;
      if (e.newValue) {
        api.me().then(applyUser).catch(() => {});
      } else {
        setUser(null);
      }
    };
    window.addEventListener('storage', handleStorage);

    // Проверяем сессию при возврате из фона (критично для Safari/Android PWA)
    // Удаляем токен только при явном 401 от сервера — не на network error
    const checkSession = () => {
      const token = localStorage.getItem('pm_token');
      if (!token) return;
      api.me().then(applyUser).catch((err) => {
        // err.status выставляется только если сервер ответил 401/403
        // network error / timeout не имеет статуса — не трогаем токен
        if (err?.status === 401 || err?.status === 403) {
          localStorage.removeItem('pm_token');
          setUser(null);
        }
        // иначе — тихо игнорируем (нет сети, таймаут и т.п.)
      });
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkSession();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Периодическая проверка каждые 30 сек — ловим удаление без переключения вкладок
    const interval = setInterval(checkSession, 30_000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, []);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    localStorage.setItem('pm_token', data.token);
    // Берём актуальные данные из БД (включая role_new → role)
    const me = await api.me();
    applyUser(me); // applyUser вызывает identify() → привязка к домену ОРГи
    Analytics.login();
    return data;
  };

  // Вход с готовым токеном и объектом пользователя (демо + регистрация + онбординг).
  //
  // is_demo приводим к булеву БЕЗ дефолта. Раньше здесь стояло `?? true`, и это
  // ломало регистрацию: ответ /register/complete поля is_demo не содержит, то есть
  // свежий тенант получал is_demo=true и уезжал мимо онбординга на Дашборд с
  // демо-туром, а в PostHog уходил как демо. Демо-флоу дефолт не нужен — ручка
  // /demo/verify отдаёт is_demo: true явным полем (routes/demo.js).
  const loginWithToken = (token, user) => {
    localStorage.setItem('pm_token', token);
    applyUser({ ...user, is_demo: !!user.is_demo }); // applyUser → identify()
  };

  const register = async (name, email, password) => {
    const data = await api.register(name, email, password);
    localStorage.setItem('pm_token', data.token);
    setUser(data.user);
    return data;
  };

  // Полный выход: сессия отзывается на сервере (POST /auth/logout), а не только
  // стирается из localStorage текущего origin. Иначе живая копия токена на
  // app.<домен> (от входа до переезда на поддомен тенанта) возвращала человека
  // в аккаунт без пароля. Голый fetch, а не api.post: клиент на 401 сам делает
  // редирект, а нам нужен тихий вызов. keepalive — запрос переживает
  // navigate/перезагрузку, которые идут сразу следом.
  const logout = () => {
    Analytics.logout();
    reset();
    let token = null;
    try { token = localStorage.getItem('pm_token'); } catch { /* приватный режим */ }
    if (token) {
      try {
        fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          keepalive: true,
        }).catch(() => {});
      } catch { /* невалидный заголовок — токен всё равно ни на что не годен */ }
    }
    localStorage.removeItem('pm_token');
    thumbCacheClear(); // превью и метаданные документов не должны переживать logout
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
